<script setup lang="ts">
import type { Component } from 'vue';
import { computed } from 'vue';
import {
  CircleCheckBig,
  Info,
  Home,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon = (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileCheck;
const DefaultTickIcon = CircleCheckBig;

/** Map Wagtail help icon keys to Lucide components (matches backend HELP_ICON_CHOICES). */
const HELP_ICON_MAP: Record<string, Component> = {
  'circle-check-big': CircleCheckBig,
  info: Info,
  home: Home,
  building: BuildingIcon ?? Building2,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': FileTextIcon ?? FileCheck,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  quote: Quote,
};

function iconComponent(iconName: string | null | undefined): Component {
  if (!iconName || typeof iconName !== 'string') return DefaultTickIcon;
  return HELP_ICON_MAP[iconName] ?? DefaultTickIcon;
}

type TickCard = { title?: string | null; text?: string | null; icon?: string | null };

const props = defineProps<{
  /** API: array. Block-picker preview: JSON string in query `cards`. */
  cards?: TickCard[] | string | null;
  /** When true, apply card surface (background + border). Default / omitted = transparent. */
  show_card_background?: boolean | string | null;
}>();

const useCardBackground = computed(() => {
  const v = props.show_card_background;
  return v === true || v === 'true';
});

function normalizeCardsInput(raw: TickCard[] | string | null | undefined): TickCard[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as TickCard[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const items = computed(() => {
  const raw = normalizeCardsInput(props.cards);
  return raw.filter(
    (c) => c && typeof c.title === 'string' && c.title.trim().length > 0
  );
});
</script>

<template>
  <div v-if="items.length" class="block-tick-features">
    <ul class="block-tick-features__grid">
      <li
        v-for="(card, index) in items"
        :key="index"
        class="block-tick-features__card"
        :class="{ 'block-tick-features__card--filled': useCardBackground }"
      >
        <component
          :is="iconComponent(card.icon)"
          class="block-tick-features__icon"
          aria-hidden="true"
        />
        <div class="block-tick-features__body">
          <h3 class="block-tick-features__title">{{ card.title }}</h3>
          <p v-if="card.text" class="block-tick-features__text">{{ card.text }}</p>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.block-tick-features {
  margin: 0;
  padding-top: 1rem;
}

.block-tick-features__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 640px) {
  .block-tick-features__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
  }
}

.block-tick-features__card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
}

.block-tick-features__card--filled {
  padding: 1.5rem;
  background: var(--color-card-bg, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.5rem;
}

.block-tick-features__icon {
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
  color: var(--color-link, #1f7a5a);
}

.block-tick-features__body {
  min-width: 0;
}

.block-tick-features__title {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  color: var(--color-text, #1a1a1a);
}

.block-tick-features__text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.625;
  color: var(--color-text-muted, #666);
}
</style>
